import {
  COLOR_CODING_NONE,
  type ColorCodingMetricType,
  type ColorCodingRange,
  type ColorCodingTemplate,
} from "@models/ColorCoding";
import { COLOR_CODING_TEMPLATES } from "@_constants/color-coding-templates";

export type ColorCodingQuestionType =
  | "Single"
  | "Multiple"
  | "Float"
  | "Number"
  | string;

export const getMetricTypeFromQuestionType = (
  questionType: ColorCodingQuestionType | null | undefined,
): ColorCodingMetricType | null => {
  const normalized = questionType?.toLowerCase();
  if (normalized === "single") return "proportion";
  if (
    normalized === "float" ||
    normalized === "numeric" ||
    normalized === "number"
  ) {
    return "mean";
  }
  return null;
};

/** Look up a template by id (ignoring the "none" sentinel). */
export const getColorCodingTemplateById = (
  templateId: string | null | undefined,
): ColorCodingTemplate | null => {
  if (!templateId || templateId === COLOR_CODING_NONE) return null;
  return COLOR_CODING_TEMPLATES.find((t) => t.id === templateId) ?? null;
};

/**
 * Templates whose metric type matches the visualization's current metric type.
 * When metricType is null, no templates are eligible (dropdown shows only None).
 */
export const getTemplatesForMetricType = (
  metricType: ColorCodingMetricType | null,
): ColorCodingTemplate[] => {
  if (!metricType) return [];
  return COLOR_CODING_TEMPLATES.filter((t) => t.metricType === metricType);
};

/**
 * Resolve the active template for rendering: the selected template only when it
 * exists AND its metric type matches the current visualization metric type.
 * Otherwise null (None selected, or graceful fallback on metric-type mismatch).
 */
export const resolveActiveColorTemplate = (
  templateId: string | null | undefined,
  metricType: ColorCodingMetricType | null,
): ColorCodingTemplate | null => {
  const template = getColorCodingTemplateById(templateId);
  if (!template) return null;
  if (!metricType || template.metricType !== metricType) return null;
  return template;
};

/** True when a saved template id is set but incompatible with the metric type. */
export const isColorTemplateMismatch = (
  templateId: string | null | undefined,
  metricType: ColorCodingMetricType | null,
): boolean => {
  const template = getColorCodingTemplateById(templateId);
  if (!template) return false;
  return !metricType || template.metricType !== metricType;
};

/** Half-up rounding to the nearest integer (e.g. 65.5 -> 66, 65.4 -> 65). */
export const roundHalfUp = (value: number): number =>
  Math.floor(value + 0.5);

/**
 * Find the range bucket a numeric value falls into, using the half-up-rounded
 * integer. Returns null when no bucket matches (value rendered without color).
 */
export const matchColorRange = (
  value: number | null | undefined,
  template: ColorCodingTemplate | null,
): ColorCodingRange | null => {
  if (template == null) return null;
  if (value == null || typeof value !== "number" || Number.isNaN(value)) {
    return null;
  }
  const rounded = roundHalfUp(value);
  return (
    template.ranges.find((r) => rounded >= r.min && rounded <= r.max) ?? null
  );
};
